import { html, View } from 'rune-ts';
import klass from './ShopView.module.scss';
import { classes } from '@services/MarppleShop/shared/util';
import { font } from '@services/MarppleShop/shared/typography';
import { makeUtilScreenNavigate } from '@services/MarppleShop/shared/app/navigate.ts';
import { dataStr } from '@services/MarppleShop/shared/util/dataStr.ts';
import { pipe } from '@fxts/core';
import { CountViewView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/CountViewView.ts';
import { CardProductError } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/CardProductView.ts';
import { removeDLE } from '@services/MarppleShop/shared/util/removeDLE.ts';
import { DetachedCountViewView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/DetachedCountViewView.ts';
import { CreatorNameView } from '@services/MarppleShop/renderApp/components/cells/CreatorName/CreatorNameView.ts';
import { getResizedUrl } from '@services/MarppleShop/shared/util/image.ts';

export type TProps =
  | {
      type: 'Text';
      theme: 'Light';
      size: '11px';
      store_name: string;
      domain_name: string;
      is_partner: boolean;
    }
  | {
      type: 'Text';
      theme: 'Light';
      size: '12px';
      store_name: string;
      domain_name: string;
      is_partner: boolean;
    }
  | {
      type: 'Text';
      theme: 'Light';
      size: '13px';
      store_name: string;
      domain_name: string;
      is_partner: boolean;
    }
  | {
      type: 'Box';
      theme: 'Light';
      size: '11px';
      store_name: string;
      domain_name: string;
      profile_image_url: string | null;
      is_partner: boolean;
    }
  | {
      type: 'Box';
      theme: 'Dark';
      size: '11px';
      store_name: string;
      domain_name: string;
      profile_image_url: string | null;
      is_partner: boolean;
    }
  | {
      type: 'Box';
      theme: 'Light';
      size: '13px';
      store_name: string;
      domain_name: string;
      profile_image_url: string | null;
      watcher_count: number | undefined;
      is_partner: boolean;
    }
  | {
      type: 'Box';
      theme: 'Dark';
      size: '13px';
      store_name: string;
      domain_name: string;
      profile_image_url: string | null;
      watcher_count: number | undefined;
      is_partner: boolean;
    }
  | {
      type: 'Line';
      theme: 'Light';
      size: '11px';
      store_name: string;
      domain_name: string;
      profile_image_url: string | null;
      is_partner: boolean;
    }
  | {
      type: 'Line';
      theme: 'Light';
      size: '13px';
      store_name: string;
      domain_name: string;
      profile_image_url: string | null;
      watcher_count: number | undefined;
      is_partner: boolean;
    };

/**
 * https://www.figma.com/design/Top00pi5q5ylSxEviOOciV/MarppleShop-Design-System?node-id=26351-75048&m=dev
 */
export class ShopView extends View<TProps> {
  #resizeProfileImage(url: string, size: number) {
    return getResizedUrl({ url, width: size * 2, format: 'webp', quality: 85 });
  }

  protected override template(props: TProps) {
    const { is_partner } = props;

    const href = `/${T.lang}/${props.domain_name}`;

    const data_post_message = pipe(href, makeUtilScreenNavigate, dataStr);

    const enhancedStoreName = pipe(props.store_name, removeDLE, html.preventEscape);

    if (props.type === 'Text' && props.theme === 'Light' && props.size === '11px') {
      return html`<a
        class="${classes(klass.shop_type_text, font('11_semibold'))}"
        href="${href}"
        data-post-message="${data_post_message}"
      >
        ${new CreatorNameView({
          name: enhancedStoreName,
          size: 'xSmall',
          is_partner,
        })}
      </a>`;
    } else if (props.type === 'Text' && props.theme === 'Light' && props.size === '12px') {
      return html`<a
        class="${classes(klass.shop_type_text, font('12_semibold'))}"
        href="${href}"
        data-post-message="${data_post_message}"
      >
        ${new CreatorNameView({
          name: enhancedStoreName,
          size: 'xSmall',
          is_partner,
        })}
      </a>`;
    } else if (props.type === 'Text' && props.theme === 'Light' && props.size === '13px') {
      return html`<a
        class="${classes(klass.shop_type_text, font('13_semibold'))}"
        href="${href}"
        data-post-message="${data_post_message}"
      >
        ${new CreatorNameView({
          name: enhancedStoreName,
          size: 'small',
          is_partner,
        })}
      </a>`;
    } else if (props.type === 'Box' && props.theme === 'Light' && props.size === '11px') {
      const { profile_image_url } = props;

      return html`<a
        class="${classes(klass.shop_type_box, klass.size_11px, klass.theme_light)}"
        href="${href}"
        data-post-message="${data_post_message}"
      >
        ${profile_image_url
          ? html`<img
              alt=""
              src="${this.#resizeProfileImage(profile_image_url, 18)}"
              class="${classes(klass.profile, klass.size_11px)}"
            />`
          : null}
        ${new CreatorNameView({
          name: html`<p
            class="${classes(
              klass.creator_shop_name_text,
              klass.size_11px,
              klass.theme_light,
              font('11_medium'),
            )}"
          >
            ${enhancedStoreName}
          </p>`,
          size: 'xSmall',
          is_partner,
        })}
      </a>`;
    } else if (props.type === 'Box' && props.theme === 'Dark' && props.size === '11px') {
      const { profile_image_url } = props;

      return html`<a
        class="${classes(klass.shop_type_box, klass.size_11px, klass.theme_dark)}"
        href="${href}"
        data-post-message="${data_post_message}"
      >
        ${profile_image_url
          ? html`<img
              alt=""
              src="${this.#resizeProfileImage(profile_image_url, 18)}"
              class="${classes(klass.profile, klass.size_11px)}"
            />`
          : null}
        ${new CreatorNameView({
          name: html` <p
            class="${classes(
              klass.creator_shop_name_text,
              klass.size_11px,
              klass.theme_dark,
              font('11_medium'),
            )}"
          >
            ${enhancedStoreName}
          </p>`,
          size: 'xSmall',
          is_partner,
        })}
      </a>`;
    } else if (props.type === 'Box' && props.theme === 'Light' && props.size === '13px') {
      const { profile_image_url, watcher_count } = props;
      return html`<a
        class="${classes(klass.shop_type_box, klass.size_13px, klass.theme_light)}"
        href="${href}"
        data-post-message="${data_post_message}"
      >
        ${profile_image_url
          ? html`<img
              alt=""
              src="${this.#resizeProfileImage(profile_image_url, 24)}"
              class="${classes(klass.profile, klass.size_13px)}"
            />`
          : null}
        ${new CreatorNameView({
          name: html`<div class="${klass.creator_shop_name_wrapper}">
            <span
              class="${classes(
                klass.creator_shop_name_text,
                klass.size_13px,
                klass.theme_light,
                font('13_medium'),
              )}"
            >
              ${enhancedStoreName}
            </span>
          </div>`,
          size: 'small',
          is_partner,
        })}
        ${watcher_count
          ? html`<span class="${classes(klass.count_view, klass.theme_light, font('12_medium'))}">
              ${ET('card_product::shop::watching', { watcher_count })}
            </span>`
          : null}
      </a>`;
    } else if (props.type === 'Box' && props.theme === 'Dark' && props.size === '13px') {
      const { profile_image_url, watcher_count } = props;

      return html`<a
        class="${classes(klass.shop_type_box, klass.size_13px, klass.theme_dark)}"
        href="${href}"
        data-post-message="${data_post_message}"
      >
        ${profile_image_url
          ? html`<img
              alt=""
              src="${this.#resizeProfileImage(profile_image_url, 24)}"
              class="${classes(klass.profile, klass.size_13px)}"
            />`
          : null}
        ${new CreatorNameView({
          name: html`<div class="${klass.creator_shop_name_wrapper}">
            <span
              class="${classes(
                klass.creator_shop_name_text,
                klass.size_13px,
                klass.theme_dark,
                font('13_medium'),
              )}"
              >${enhancedStoreName}</span
            >
          </div>`,
          size: 'small',
          is_partner,
        })}
        ${watcher_count
          ? new DetachedCountViewView({
              size: 'Medium',
              watcher_count,
            })
          : null}
      </a>`;
    } else if (props.type === 'Line' && props.theme === 'Light' && props.size === '11px') {
      const { profile_image_url } = props;

      return html`<a
        class="${klass.shop_type_line_size_11px}"
        href="${href}"
        data-post-message="${data_post_message}"
      >
        ${profile_image_url
          ? html`<img
              alt=""
              src="${this.#resizeProfileImage(profile_image_url, 18)}"
              class="${classes(klass.profile, klass.size_11px)}"
            />`
          : null}
        ${new CreatorNameView({
          name: html`<span class="${classes(klass.creator_shop_name_text, font('12_medium'))}"
            >${enhancedStoreName}</span
          >`,
          size: 'xSmall',
          is_partner,
        })}
      </a>`;
    } else if (props.type === 'Line' && props.theme === 'Light' && props.size === '13px') {
      const { profile_image_url, watcher_count } = props;

      return html`
        <a class="${klass.shop_type_line_size_13px}" href="${href}" data-post-message="${data_post_message}">
          ${profile_image_url
            ? html`<img
                alt=""
                src="${this.#resizeProfileImage(profile_image_url, 24)}"
                class="${classes(klass.profile, klass.size_13px)}"
              />`
            : null}
          ${new CreatorNameView({
            name: html`<span class="${classes(klass.creator_shop_name_text, font('12_medium'))}"
              >${enhancedStoreName}</span
            >`,
            size: 'small',
            is_partner,
          })}
          ${watcher_count
            ? html`<span class="${klass.count_view_wrapper}"
                >${new CountViewView({ size: 'Medium', watcher_count })}</span
              >`
            : null}
        </a>
      `;
    } else {
      throw new CardProductError();
    }
  }
}
